/**
 * Format number with commas for display
 * Example: 1234567.89 -> "1,234,567.89"
 * Limits decimal places to 2 digits
 * Preserves trailing decimal point (e.g., "5." stays as "5.")
 */
export const formatNumberWithCommas = (value: string | number): string => {
  const stringValue = String(value);
  // Remove all non-digit, non-decimal, and non-minus characters
  const cleaned = stringValue.replace(/[^\d.-]/g, '');

  if (!cleaned) return '';

  // Handle special case of just a decimal point or minus sign
  if (cleaned === '.' || cleaned === '-' || cleaned === '-.') return cleaned;

  // Check if input ends with a decimal point (user is about to type decimal places)
  const endsWithDecimal = cleaned.endsWith('.');

  // Split into integer and decimal parts
  const parts = cleaned.split('.');
  const integerPart = parts[0];
  let decimalPart = parts[1];

  // Limit decimal part to 2 digits
  if (decimalPart !== undefined && decimalPart.length > 2) {
    decimalPart = decimalPart.substring(0, 2);
  }

  // Add commas to integer part (handle negative numbers)
  const isNegative = integerPart.startsWith('-');
  const absIntegerPart = isNegative ? integerPart.slice(1) : integerPart;
  const formatted = absIntegerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  const signedFormatted = isNegative ? `-${formatted}` : formatted;

  // Reconstruct with decimal if present
  if (decimalPart !== undefined) {
    return `${signedFormatted}.${decimalPart}`;
  } else if (endsWithDecimal) {
    // Preserve trailing decimal point
    return `${signedFormatted}.`;
  } else {
    return signedFormatted;
  }
};

/**
 * Remove commas to get raw number string
 * Example: "1,234,567.89" -> "1234567.89"
 */
export const removeCommas = (value: string): string => {
  return value.replace(/,/g, '');
};

/**
 * Parse a formatted number string to a number
 * Example: "1,234,567.89" -> 1234567.89
 * Returns 0 for invalid inputs, but preserves special cases like "-" and "."
 */
export const parseFormattedNumber = (value: string): number => {
  // Handle empty or special input cases
  if (!value || value === '-' || value === '.' || value === '-.') {
    return 0;
  }

  const cleaned = removeCommas(value);
  const parsed = parseFloat(cleaned);
  return isNaN(parsed) ? 0 : parsed;
};

// Spellings (incl. geresh/gershayim variants) -> canonical UNITS entry. ק"ג, שרות and תא are no
// longer offered in dropdowns but stay here so pasted rows still detect them as the unit cell.
const UNIT_ALIASES: Record<string, string> = {
  "יח'": "יח'",
  'יח׳': "יח'",
  יחידה: "יח'",
  יחידות: "יח'",
  "מ'": "מ'",
  'מ׳': "מ'",
  מטר: "מ'",
  מטרים: "מ'",
  'מ"א': "מ'",
  'מ״א': "מ'",
  מ2: 'מ"ר',
  'מ"ר': 'מ"ר',
  'מ״ר': 'מ"ר',
  "ק'ג": 'ק"ג',
  'ק"ג': 'ק"ג',
  קילו: 'ק"ג',
  קילוגרם: 'ק"ג',
  שעה: 'שעה',
  שעות: 'שעה',
  "ש'ע": 'שעה',
  יום: 'יום',
  ימים: 'יום',
  חודש: 'חודש',
  חודשים: 'חודש',
  קומפלט: 'קומפלט',
  קומפ: 'קומפלט',
  "קומפ'": 'קומפלט',
  'קומפ׳': 'קומפלט',
  קומ: 'קומפלט',
  קו: 'קומפלט',
  CMP: 'קומפלט',
  cmp: 'קומפלט',
  שרות: 'שרות',
  שירות: 'שרות',
  תא: 'תא',
  "נק'": "נק'",
  'נק׳': "נק'",
  נקודה: "נק'",
  נקודות: "נק'",
  קוב: 'קוב',
  קובים: 'קוב',
  'מ"ק': 'קוב',
  'מ״ק': 'קוב',
};

/**
 * Parse a multi-line price list pasted from clipboard into an array of numbers.
 * Handles formats like "₪30", "₪15,000", "3,500" etc.
 * Returns null when there is only one non-empty line (let normal paste proceed).
 */
export const parsePriceListPaste = (text: string): number[] | null => {
  if (!text) return null;
  const lines = text
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')
    .split('\n')
    .map((l) => l.trim())
    .filter((l) => l.length > 0);
  if (lines.length < 2) return null;
  return lines.map((line) => {
    const cleaned = line.replace(/[₪\s]/g, '');
    return parseFormattedNumber(cleaned);
  });
};

const isNumericCell = (value: string): boolean => {
  const cleaned = value.replace(/,/g, '').trim();
  return /^-?(?:\d+\.?\d*|\.\d+)$/.test(cleaned);
};

// Some BoQ Excel exports serialize RTL cells in visual-LTR order: a description
// that reads naturally as "תעלה כנ"ל 25X30 מ"מ" arrives on the clipboard as
// "30 מ"מX25 תעלה כנ"ל" — dimensions/punctuation at the start, Hebrew at the
// end. CSS direction can't reorder words, so we normalize at paste time.
//
// Strict heuristic to avoid false positives: reverse only when the first
// whitespace-token is digits (optionally with a leading '.') AND the last
// token contains Hebrew. Matches the BoQ pattern (".40 ...", "30 ...")
// without touching items like "PVC 25 צינור" or pure-numeric descriptions.
const HEBREW_LETTER = /[֐-׿]/;
const REVERSED_FIRST_TOKEN = /^\.?\d/;
// Whitespace, ASCII quotes, Hebrew geresh (׳), Hebrew gershayim (״).
const SCRIPT_NEUTRAL = /[\s"'׳״]/;

function looksLikeReversedHebrewDescription(description: string): boolean {
  const trimmed = description.trim();
  if (!trimmed) return false;
  const firstSpaceIdx = trimmed.search(/\s/);
  if (firstSpaceIdx === -1) return false;
  const firstToken = trimmed.slice(0, firstSpaceIdx);
  if (!REVERSED_FIRST_TOKEN.test(firstToken)) return false;
  const tokens = trimmed.split(/\s+/);
  const lastToken = tokens[tokens.length - 1];
  return HEBREW_LETTER.test(lastToken);
}

// Group consecutive Hebrew-strong vs non-Hebrew-strong runs. Neutrals
// (whitespace, quote-like glyphs) attach to the current run so Hebrew
// abbreviations ("מ"מ", "כנ"ל") and adjacent Hebrew words ("תעלה כנ"ל")
// survive as one chunk.
function chunkByScriptDirection(s: string): string[] {
  const chunks: string[] = [];
  let buf = '';
  let bufType: 'H' | 'N' | null = null;
  for (const ch of s) {
    if (SCRIPT_NEUTRAL.test(ch)) {
      buf += ch;
      continue;
    }
    const t: 'H' | 'N' = HEBREW_LETTER.test(ch) ? 'H' : 'N';
    if (bufType === null || t === bufType) {
      buf += ch;
      bufType = t;
    } else {
      chunks.push(buf);
      buf = ch;
      bufType = t;
    }
  }
  if (buf) chunks.push(buf);
  return chunks;
}

export function normalizeBoqDescription(description: string): string {
  if (!looksLikeReversedHebrewDescription(description)) return description;
  const chunks = chunkByScriptDirection(description);
  if (chunks.length < 2) return description;
  return chunks
    .map((c) => c.trim())
    .filter(Boolean)
    .reverse()
    .join(' ');
}

/**
 * Parse tab/newline text copied from spreadsheet into item rows.
 * Returns null when clipboard text does not look like a spreadsheet payload.
 */
/**
 * Pure numeric section code, e.g. "8", "08.01", "08.01.02.0022". Real BoQs
 * often use 4-segment codes which exceed the 10-char cap applied to the
 * looser alphanumeric pattern.
 */
const NUMERIC_SECTION_PATTERN = /^\d+(?:\.\d+)*$/;

const SECTION_PATTERN = /^[\d./\-א-ת]+$/;

export function looksLikeSection(cell: string): boolean {
  // Pure numeric-dot codes get a generous cap — covers "08.01.02.0022" at 13 chars.
  if (cell.length <= 20 && NUMERIC_SECTION_PATTERN.test(cell)) return true;
  // Alphanumeric sections ("א/1", "1.2.3") keep the tighter legacy cap to
  // avoid mis-classifying short Hebrew description tokens.
  if (cell.length <= 10 && SECTION_PATTERN.test(cell)) return true;
  return false;
}

/**
 * Normalize a BoQ section code so leading-zero variants match: "08.01.02" → "8.1.2".
 * Non-numeric segments (Hebrew-letter codes like "א/1", "ב/1") are preserved verbatim
 * so lettered chapters stay distinct keys instead of all collapsing to "0".
 * Used as a join key for sections across paste/lookup surfaces.
 */
export function normSection(s: string): string {
  return s
    .split('.')
    .map((p) => {
      const trimmed = p.trim();
      return /^\d+$/.test(trimmed) ? String(parseInt(trimmed, 10)) : trimmed;
    })
    .join('.');
}

/**
 * Hierarchy key for a BoQ section code: normalize, then strip TRAILING all-zero
 * segments. Fixed-width BoQs mark levels with trailing zeros —
 * '01.00.00.0000' → '1' (chapter), '01.08.00.0000' → '1.8' (sub-chapter),
 * '01.34.01.0000' → '1.34.1', '01.34.01.0010' → '1.34.1.10' (leaf keeps its code).
 * Classic 2/3-segment codes without trailing zeros pass through as normSection.
 * Always keeps at least the first segment ('00.00' → '0'). Non-numeric segments
 * (Hebrew codes like 'א/1') are preserved by normSection and never equal '0',
 * so they are never stripped. '' → ''.
 */
export function sectionHierarchyKey(section: string | undefined): string {
  const norm = normSection(section || '');
  if (!norm) return '';
  const parts = norm.split('.');
  let end = parts.length;
  while (end > 1 && parts[end - 1] === '0') end--;
  return parts.slice(0, end).join('.');
}

/** Depth of a section's hierarchy key (segment count); 0 for empty/undefined. */
export function sectionHierarchyDepth(section: string | undefined): number {
  const key = sectionHierarchyKey(section);
  return key ? key.split('.').length : 0;
}

export const parseExcelPaste = (
  text: string,
  options?: { skipSections?: boolean }
): Array<{
  description: string;
  quantity: number;
  unit?: string;
  section?: string;
  price?: number;
  isHeader?: boolean;
}> | null => {
  if (!text) return null;

  const skipSections = options?.skipSections ?? false;

  const normalized = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  const rows = normalized
    .split('\n')
    .map((row) => row.trim())
    .filter((row) => row.length > 0);

  // Keep normal paste behavior for plain single-cell text.
  if (rows.length === 0 || !normalized.includes('\t')) return null;

  const parsedRows: Array<{
    description: string;
    quantity: number;
    unit?: string;
    section?: string;
    price?: number;
    isHeader?: boolean;
  }> = [];

  for (const row of rows) {
    const cells = row
      .split('\t')
      .map((cell) => cell.trim())
      .filter(Boolean);
    if (cells.length === 0) continue;

    // Detect 2-cell header rows like "8\tמערכות ומתקני חשמל" (only when sections enabled)
    if (!skipSections && cells.length === 2) {
      const firstIsSection = looksLikeSection(cells[0]);
      const secondIsText =
        !isNumericCell(cells[1]) && !UNIT_ALIASES[cells[1].replace(/['"]/g, "'").trim()];
      if (firstIsSection && secondIsText) {
        parsedRows.push({
          description: normalizeBoqDescription(cells[1]),
          quantity: 0,
          section: cells[0],
          isHeader: true,
        });
        continue;
      }
    }

    let quantity = 0;
    let hasQuantity = false;
    let unit: string | undefined;
    let hasUnit = false;
    const descriptionCandidates: string[] = [];
    const numericValues: number[] = [];

    for (const cell of cells) {
      if (isNumericCell(cell)) {
        const n = parseFormattedNumber(cell);
        numericValues.push(n);
        if (!hasQuantity) {
          quantity = n;
          hasQuantity = true;
          continue;
        }
        // Fall through: 2nd+ numerics still go through unit/desc handling so the
        // section detector below can pick a short numeric ("8", "01") as a section
        // code if the row turns out to be section + numeric description.
      }

      const normalizedCell = cell.replace(/['"]/g, "'").trim();
      const matchedUnit = UNIT_ALIASES[normalizedCell] || UNIT_ALIASES[cell];
      if (matchedUnit && !hasUnit) {
        unit = matchedUnit;
        hasUnit = true;
        continue;
      }

      descriptionCandidates.push(cell);
    }

    // Among text candidates, detect a short section-like value (only when sections enabled)
    let section: string | undefined;
    let description = '';
    if (!skipSections && descriptionCandidates.length >= 2) {
      const shortCandidate = descriptionCandidates.find((c) => looksLikeSection(c));
      if (shortCandidate) {
        section = shortCandidate;
        const remaining = descriptionCandidates.filter((c) => c !== shortCandidate);
        description = remaining.sort((a, b) => b.length - a.length)[0] || '';
      } else {
        description = descriptionCandidates.sort((a, b) => b.length - a.length)[0] || '';
      }
    } else {
      description = descriptionCandidates.sort((a, b) => b.length - a.length)[0] || '';
    }
    if (!description) continue;

    // Priced-BoQ heuristic: when the row has exactly two numeric cells (the common
    // `quantity | price` layout), surface the second as a unit price. With 3+ numerics
    // we can't tell qty / price / total apart safely, so we'd rather leave price empty
    // than land the wrong column silently.
    const price = numericValues.length === 2 ? numericValues[1] : undefined;

    parsedRows.push({
      description: normalizeBoqDescription(description),
      quantity: hasQuantity ? quantity : 0,
      ...(unit && { unit }),
      ...(section && { section }),
      ...(price !== undefined && { price }),
    });
  }

  return parsedRows.length > 0 ? parsedRows : null;
};

/**
 * Strip ₪ / spaces from a cell before checking whether it parses as a number.
 * Mirrors `parsePriceListPaste`'s tolerance so currency-prefixed cells still
 * count as numeric for the purpose of locating the price column.
 */
const stripPriceDecoration = (s: string): string => s.replace(/[₪\s]/g, '');

const cellIsNumericPrice = (s: string): boolean => isNumericCell(stripPriceDecoration(s));

/**
 * Section candidate must start with a digit AND contain a dot — guards
 * against the broader `looksLikeSection` regex matching pure-Hebrew cells
 * ("תיאור") or bare integers ("200") that are really descriptions or prices.
 * BoQ section codes always have multi-segment dot notation ("08.01",
 * "08.010.0001"), so requiring a dot is safe.
 */
const cellIsSectionCandidate = (s: string): boolean =>
  /^\d/.test(s) && s.includes('.') && looksLikeSection(s);

/**
 * Parse tab/newline text copied from a spreadsheet into reference-price rows.
 * Each row needs at least one identifier (section or description) and a price.
 * The price is the LAST numeric cell excluding the section cell — quantity
 * cells (numeric, but earlier in the row) are ignored. Returns null when the
 * input is not tab-delimited or yields zero usable rows.
 */
export const parseTargetPricesPaste = (
  text: string
): Array<{ section?: string; description: string; price: number }> | null => {
  if (!text) return null;
  const normalized = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  if (!normalized.includes('\t')) return null;
  const rows = normalized
    .split('\n')
    .map((r) => r.trim())
    .filter(Boolean);
  if (rows.length === 0) return null;

  const out: Array<{ section?: string; description: string; price: number }> = [];
  for (const row of rows) {
    const cells = row
      .split('\t')
      .map((c) => c.trim())
      .filter(Boolean);
    if (cells.length < 2) continue;

    // Classify each cell once.
    const isSecCand = cells.map(cellIsSectionCandidate);
    const isNumPrice = cells.map(cellIsNumericPrice);

    // Price: prefer the LAST cell that is numeric AND NOT a section candidate
    // (a clean price column). Fall back to the last numeric cell even if it's
    // section-shaped — covers description+decimal-price rows where the only
    // numeric cell is e.g. "200.5".
    let priceIdx = -1;
    for (let i = cells.length - 1; i >= 0; i--) {
      if (isNumPrice[i] && !isSecCand[i]) {
        priceIdx = i;
        break;
      }
    }
    if (priceIdx === -1) {
      for (let i = cells.length - 1; i >= 0; i--) {
        if (isNumPrice[i]) {
          priceIdx = i;
          break;
        }
      }
    }
    if (priceIdx === -1) continue;
    const price = parseFormattedNumber(stripPriceDecoration(cells[priceIdx]));
    if (!Number.isFinite(price) || price <= 0) continue;

    // Section: first section-candidate cell excluding the price cell.
    let sectionIdx = -1;
    let section: string | undefined;
    for (let i = 0; i < cells.length; i++) {
      if (i === priceIdx) continue;
      if (isSecCand[i]) {
        sectionIdx = i;
        section = cells[i];
        break;
      }
    }

    // Description: longest non-section, non-numeric, non-unit cell.
    let description = '';
    for (let i = 0; i < cells.length; i++) {
      if (i === sectionIdx || i === priceIdx) continue;
      const cell = cells[i];
      if (cellIsNumericPrice(cell)) continue;
      const normalizedCell = cell.replace(/['"]/g, "'").trim();
      if (UNIT_ALIASES[normalizedCell] || UNIT_ALIASES[cell]) continue;
      if (cell.length > description.length) description = cell;
    }

    // Require at least one identifier alongside the price.
    if (!section && !description) continue;

    out.push({
      ...(section && { section }),
      description: description ? normalizeBoqDescription(description) : '',
      price,
    });
  }

  return out.length > 0 ? out : null;
};
